import React, { useContext, useMemo } from 'react';
import { Route, Switch } from 'react-router-dom';
import { NexusProvider } from '@return-rabbit/nexus';
import { Helmet } from 'react-helmet';
import _get from 'lodash/get';

import { logError } from 'utils';
import { ErrorBoundary } from 'components';
import Layout from './Layout';
import Logout from './views/Logout';
import { ErrorPage } from 'views/Errors';
import QrShippingLabel from './views/QrShippingLabel';
import DropoffLocations from './views/DropoffLocations';
import ShipmentTrackingApp from 'views/ShipmentTrackingApp';
import { AuthContext } from 'context/AuthContext';
import { useNexusConfig, useSegment } from 'utils/hooks';

export default function App() {
  const { authToken, appType, userData, getStoreDetails } = useContext(
    AuthContext
  );

  const { apiKey } = useSegment();
  const { axiosConfig, masterData, storeProperties } = useNexusConfig();

  const userInfo = useMemo(() => {
    if (!userData) return null;

    return {
      id: _get(userData, 'userId'),
      email: _get(userData, 'email'),
      firstName: _get(userData, 'firstName'),
      lastName: _get(userData, 'lastName')
    };
  }, [userData]);

  const storeInfo = getStoreDetails();
  const storeName = _get(storeInfo, 'name', '');
  const faviconLink = _get(storeInfo, 'favicon', '');
  const isSSRStore = _get(storeInfo, 'isSSRStore', false);

  return (
    <>
      <Helmet>
        <title>{storeName ? `${storeName} Support` : 'Returns Portal'}</title>
        {faviconLink && <link rel="shortcut icon" href={faviconLink} />}
      </Helmet>
      <ErrorBoundary>
        {appType.retex && (
          <NexusProvider
            key={authToken}
            mode="SHOPPER"
            axiosConfig={axiosConfig}
            masterData={masterData}
            storeProperties={storeProperties}
            logError={logError}
            userInfo={userInfo}
            segmentApiKey={apiKey}
          >
            <Switch>
              <Route path="/logout" component={Logout} />
              <Route path="/error" component={ErrorPage} />
              <Route exact path="/qr-shipping-label">
                <QrShippingLabel
                  storeName={storeName}
                  isSSRStore={isSSRStore}
                />
              </Route>
              <Route exact path="/dropoff-locations/:os_id">
                <DropoffLocations
                  storeName={storeName}
                  isSSRStore={isSSRStore}
                />
              </Route>
              <Route path="/" component={Layout} />
            </Switch>
          </NexusProvider>
        )}
        {appType.shipmentTracking && (
          <Switch>
            <Route path="/" component={ShipmentTrackingApp} />
          </Switch>
        )}
      </ErrorBoundary>
    </>
  );
}
